import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import NoiseOverlay from "@/components/NoiseOverlay";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { trackPageView, setUserId } from "@/lib/analytics";
import "@/App.css";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import Dashboard from "@/pages/Dashboard";
import BaselineTesting from "@/pages/BaselineTesting";
import ProgramProgress from "@/pages/ProgramProgress";
import EngineMonitor from "@/pages/EngineMonitor";
import VerticalTracker from "@/pages/VerticalTracker";
import WorkoutLibrary from "@/pages/WorkoutLibrary";
import AICoach from "@/pages/AICoach";
import Wearables from "@/pages/Wearables";
import PivotEcosystem from "@/pages/PivotEcosystem";
import Community from "@/pages/Community";
import Tournaments from "@/pages/Tournaments";
import Teams from "@/pages/Teams";
import BodyweightExercises from "@/pages/BodyweightExercises";
import WeightRoomPage from "@/pages/WeightRoomPage";
import ProgressPhotos from "@/pages/ProgressPhotos";
import WeeklySchedule from "@/pages/WeeklySchedule";
import Badges from "@/pages/Badges";
import TrainingSessions from "@/pages/TrainingSessions";
import GrindPage from "@/pages/GrindPage";
import Profile from "@/pages/Profile";
import Leaderboard from "@/pages/Leaderboard";
import Subscription from "@/pages/Subscription";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import SharePage from "@/pages/SharePage";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import Recovery from "@/pages/Recovery";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminMangaStudio from "@/pages/AdminMangaStudio";
import GlobalLeaderboard from "@/pages/GlobalLeaderboard";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CookieConsent from "@/components/CookieConsent";
import Layout from "@/components/Layout";
import { tokenStorage, userStorage } from "@/utils/safeStorage";

// Fires a GA4 page_view on every client-side route change.
function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = tokenStorage.get();
    const parsed = userStorage.get();

    if (token && parsed) {
      setUser(parsed);
      setUserId(parsed?.id || parsed?._id || parsed?.email);
    }
    setLoading(false);
  }, []);

  // Global 401 interceptor — if the JWT expires anywhere in the app, clear
  // storage, show a friendly message, and send the athlete back to login.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = (error?.response?.data?.detail || "").toLowerCase();
        const isAuthRoute = error?.config?.url?.includes("/auth/");
        if (status === 401 && !isAuthRoute && (detail.includes("token") || detail.includes("unauthorized") || detail.includes("expired"))) {
          try {
            tokenStorage.clear();
          } catch { /* noop */ }
          if (!window.location.pathname.startsWith("/login")) {
            // Preserve the page the athlete was on so we can bounce them back after login.
            const back = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `/login?reason=expired&back=${back}`;
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const handleLogin = (token, userData) => {
    tokenStorage.set(token);
    userStorage.set(userData);
    setUser(userData);
    setUserId(userData?.id || userData?._id || userData?.email);
  };

  const handleLogout = () => {
    tokenStorage.clear();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <div className="App">
        <NoiseOverlay />
        <BrowserRouter>
          <AnalyticsRouteTracker />
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={!user ? <LoginPage onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/dashboard" element={user ? <Layout user={user} onLogout={handleLogout}><Dashboard /></Layout> : <Navigate to="/login" />} />
          <Route path="/baseline" element={user ? <Layout user={user} onLogout={handleLogout}><BaselineTesting /></Layout> : <Navigate to="/login" />} />
          <Route path="/program" element={user ? <Layout user={user} onLogout={handleLogout}><ProgramProgress /></Layout> : <Navigate to="/login" />} />
          <Route path="/engine" element={user ? <Layout user={user} onLogout={handleLogout}><EngineMonitor /></Layout> : <Navigate to="/login" />} />
          <Route path="/schedule" element={user ? <Layout user={user} onLogout={handleLogout}><WeeklySchedule /></Layout> : <Navigate to="/login" />} />
          <Route path="/badges" element={user ? <Layout user={user} onLogout={handleLogout}><Badges /></Layout> : <Navigate to="/login" />} />
          <Route path="/sessions" element={user ? <Layout user={user} onLogout={handleLogout}><TrainingSessions /></Layout> : <Navigate to="/login" />} />
          <Route path="/grind" element={user ? <Layout user={user} onLogout={handleLogout}><GrindPage /></Layout> : <Navigate to="/login" />} />
          <Route path="/vertical" element={user ? <Layout user={user} onLogout={handleLogout}><VerticalTracker /></Layout> : <Navigate to="/login" />} />
          <Route path="/bodyweight" element={user ? <Layout user={user} onLogout={handleLogout}><BodyweightExercises /></Layout> : <Navigate to="/login" />} />
          <Route path="/weightroom" element={user ? <Layout user={user} onLogout={handleLogout}><WeightRoomPage /></Layout> : <Navigate to="/login" />} />
          <Route path="/progress" element={user ? <Layout user={user} onLogout={handleLogout}><ProgressPhotos /></Layout> : <Navigate to="/login" />} />
          <Route path="/library" element={user ? <Layout user={user} onLogout={handleLogout}><WorkoutLibrary /></Layout> : <Navigate to="/login" />} />
          <Route path="/coach" element={user ? <Layout user={user} onLogout={handleLogout}><AICoach /></Layout> : <Navigate to="/login" />} />
          <Route path="/wearables" element={user ? <Layout user={user} onLogout={handleLogout}><Wearables /></Layout> : <Navigate to="/login" />} />
          <Route path="/ecosystem" element={user ? <Layout user={user} onLogout={handleLogout}><PivotEcosystem /></Layout> : <Navigate to="/login" />} />
          <Route path="/community" element={user ? <Layout user={user} onLogout={handleLogout}><Community /></Layout> : <Navigate to="/login" />} />
          <Route path="/tournaments" element={user ? <Layout user={user} onLogout={handleLogout}><Tournaments /></Layout> : <Navigate to="/login" />} />
          <Route path="/teams" element={user ? <Layout user={user} onLogout={handleLogout}><Teams /></Layout> : <Navigate to="/login" />} />
          <Route path="/leaderboard" element={user ? <Layout user={user} onLogout={handleLogout}><Leaderboard /></Layout> : <Navigate to="/login" />} />
          <Route path="/subscription" element={user ? <Layout user={user} onLogout={handleLogout}><Subscription /></Layout> : <Navigate to="/login" />} />
          <Route path="/subscription/success" element={user ? <Layout user={user} onLogout={handleLogout}><SubscriptionSuccess /></Layout> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <Layout user={user} onLogout={handleLogout}><Profile /></Layout> : <Navigate to="/login" />} />
          <Route path="/share" element={user ? <Layout user={user} onLogout={handleLogout}><SharePage /></Layout> : <Navigate to="/login" />} />
          <Route path="/analytics" element={user ? <Layout user={user} onLogout={handleLogout}><AnalyticsDashboard /></Layout> : <Navigate to="/login" />} />
          <Route path="/recovery" element={user ? <Layout user={user} onLogout={handleLogout}><Recovery /></Layout> : <Navigate to="/login" />} />
          <Route path="/admin" element={user ? <Layout user={user} onLogout={handleLogout}><AdminDashboard /></Layout> : <Navigate to="/login" />} />
          <Route path="/admin/manga" element={user ? <Layout user={user} onLogout={handleLogout}><AdminMangaStudio /></Layout> : <Navigate to="/login" />} />
          <Route path="/leaderboard/global" element={user ? <Layout user={user} onLogout={handleLogout}><GlobalLeaderboard /></Layout> : <Navigate to="/login" />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
        <CookieConsent />
      </div>
    </LanguageProvider>
  );
}

export default App;